<template>
  <template v-if="invoice.type === 'pro_forma'">
    <v-icon v-if="invoice.status !== 'CONCEPT'" class="inline cursor-pointer" @click="openEmailDialog(invoice)">
      mdi-magnify
    </v-icon>
  </template>
  <template v-else-if="invoice.type === 'sales_invoice' || invoice.type === 'credit_note'">
    <v-icon class="inline cursor-pointer" @click="openEmailDialog(invoice)">
      mdi-magnify
    </v-icon>
  </template>
  <v-dialog v-model="emailDialog" max-width="1170px">
    <v-card>
      <v-card-text>

        <div v-if="emails.length === 0">
          <p class="my-3"><i>{{ t('noEmailsFound') }}</i></p>
          <v-btn v-if="invoice.status === 'PENDING' && !(invoice.type === 'pro_forma' && canSendReminder)" flat class="mt-6" @click="resendEmailDialog = true" color="primary">{{ t('sendEmail') }}</v-btn>
        </div>
        <div v-else-if="emails.length > 0" class="d-flex flex-row">
          <div>
            <v-tabs
              v-model="tab"
              color="primary"
              direction="vertical"
              class="tabs-emails"
            >
              <v-tab v-for="email in emails" :key="email.id" :value="email.id" prepend-icon="mdi-email-outline" :text="formatDateTime(email.created_at)">
                <template #append>
                  <span v-if="email.is_reminder" class="uppercase mr-2">{{ t('reminder') }}</span>
                  <span :class="{
                    'uppercase': true,
                    'text-orange font-bold': email.status === 'PENDING',
                    'text-red font-bold': email.status === 'FAILED' || email.status === 'PARTIALLY FAILED'
                  }">{{ t('emailStatuses.' + email.status) }}</span>
                </template>
              </v-tab>
            </v-tabs>
            <div>
              <v-btn prepend-icon="mdi-refresh" flat class="mt-6" @click="refreshData()">{{ t('resfreshData') }}</v-btn>
            </div>
            <div>
              <v-btn v-if="auth.check('edit job financial documents') && ((invoice.type === 'pro_forma' && invoice.status === 'PENDING' && !canSendReminder) || invoice.type === 'sales_invoice' || invoice.type === 'credit_note')" flat class="mt-6" @click="resendEmailDialog = true" color="primary">{{ t('resendEmail') }}</v-btn>
            </div>
            <div>
              <template v-if="auth.check('edit job financial documents') && invoice.type === 'pro_forma' && invoice.status === 'PENDING'">
                <v-btn flat class="mt-6 d-block" :disabled="!canSendReminder" @click="openSendReminderDialog()" color="primary">{{ t('sendReminder') }}</v-btn>
                <p v-if="!canSendReminder && reminderAvailableFrom" class="mt-2"><small><i>{{ t('reminderAvailableFrom', {'date': formatDate(reminderAvailableFrom)}) }}</i></small></p>
              </template>
            </div>
          </div>

          <v-tabs-window v-model="tab">
            <v-tabs-window-item v-for="email in emails" :key="email.id" :value="email.id">
              <v-card flat>
                <v-card-text>
                  <table class="email-details-table">
                    <tbody>
                    <tr>
                      <td colspan="2" class="text-lg font-bold">{{ email.subject }}</td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('sentTo') }}:</td>
                      <td>
                        <span v-for="(to, index) in email.to" :key="index">
                          <template v-if="to.name">{{ to.name }} &lt;{{ to.address }}&gt;</template><template v-else>{{ to.address }}</template>{{ index < email.to.length - 1 ? ', ' : '' }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('cc') }}:</td>
                      <td>
                        <template v-if="email.cc.length === 0">
                          &ndash;
                        </template>
                        <template v-else>
                          <span v-for="(cc, index) in email.cc" :key="index">
                            <template v-if="cc.name">{{ cc.name }} &lt;{{ cc.address }}&gt;</template><template v-else>{{ cc.address }}</template>{{ index < email.cc.length - 1 ? ', ' : '' }}
                          </span>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('bcc') }}:</td>
                      <td>
                        <template v-if="email.bcc.length === 0">
                          &ndash;
                        </template>
                        <template v-else>
                          <span v-for="(bcc, index) in email.bcc" :key="index">
                            <template v-if="bcc.name">{{ bcc.name }} &lt;{{ bcc.address }}&gt;</template><template v-else>{{ bcc.address }}</template>{{ index < email.bcc.length - 1 ? ', ' : '' }}
                          </span>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('from') }}:</td>
                      <td>
                        <span v-for="(from, index) in email.from" :key="index">
                          <template v-if="from.name">{{ from.name }} &lt;{{ from.address }}&gt;</template><template v-else>{{ from.address }}</template>{{ index < email.from.length - 1 ? ', ' : '' }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('replyTo') }}:</td>
                      <td>
                        <span v-for="(replyTo, index) in email.reply_to" :key="index">
                          <template v-if="replyTo.name">{{ replyTo.name }} &lt;{{ replyTo.address }}&gt;</template><template v-else>{{ replyTo.address }}</template>{{ index < email.reply_to.length - 1 ? ', ' : '' }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('priority') }}:</td>
                      <td>
                        <span :class="{ 'font-bold': email.priority === 'high' }">{{ t('emailPriorities.' + (email.priority ?? 'normal')) }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('sentAt') }}:</td>
                      <td>{{ formatDateTime(email.created_at) }}</td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('attachments') }}:</td>
                      <td>
                        <template v-if="email.attachments.length === 0">
                          &ndash;
                        </template>
                        <template v-else>
                          <span v-for="(attachment, index) in email.attachments" :key="index">
                            <template v-if="attachment.disposition === 'attachment'">{{ attachment.name }}<br></template>
                          </span>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('sentVia') }}:</td>
                      <td>{{ email.service }}</td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('messageId') }}:</td>
                      <td>{{ email.message_id }}</td>
                    </tr>
                    <tr>
                      <td class="nowrap">{{ t('status') }}:</td>
                      <td>
                        <span :class="{
                          'uppercase': true,
                          'text-orange font-bold': email.status === 'PENDING',
                          'text-red font-bold': email.status === 'FAILED' || email.status === 'PARTIALLY FAILED'
                        }">{{ t('emailStatuses.' + email.status) }}</span> - <i>{{  t('seeDetailedStatusHistoryBelowEmail') }}</i>
                      </td>
                    </tr>
                    </tbody>
                  </table>

                  <v-tabs v-model="innerTab">
                    <v-tab value="html">{{ t('htmlVersion') }}</v-tab>
                    <v-tab value="text">{{ t('plainTextVersion') }}</v-tab>
                  </v-tabs>

                  <v-tabs-window v-model="innerTab" class="inner-tab-window mb-6">
                    <v-tabs-window-item value="html">
                      <div v-html="email.body_html" class="email-html"></div>
                    </v-tabs-window-item>
                    <v-tabs-window-item value="text">
                      <div class="px-4 py-4 email-plain-text">
                        <div v-html="email.body_text"></div>
                      </div>
                    </v-tabs-window-item>
                  </v-tabs-window>

                  <table class="email-details-table">
                    <tbody>
                    <template v-for="groupedEvent in email.groupedEvents">
                      <tr>
                        <td colspan="3" class="pt-4">
                          <span class="font-bold">{{ groupedEvent[0].recipient }}</span> - {{ t('statusHistory') }}
                        </td>
                      </tr>
                      <tr v-for="event in groupedEvent" :key="event.id">
                        <td class="nowrap">{{ formatDateTimeV2(event.occurred_at) }} uur:</td>
                        <td class="nowrap uppercase">
                          <span :class="{
                            'uppercase': true,
                            'text-orange font-bold': event.event === 'PENDING' || event.event === 'TEMPORARILY FAILED',
                            'text-red font-bold': event.event === 'FAILED'
                          }">{{ t('emailStatuses.' + event.event) }}</span>
                        </td>
                        <td><small>{{ event.metadata }}</small></td>
                      </tr>
                    </template>
                    </tbody>
                  </table>

                  <template v-if="email.service === 'mailgun'">
                    <p><small><i>{{ t('openedStatusesDependsOnRecipientsEmailClient') }}</i></small></p>
                    <v-btn v-if="!isManuallyUpdatingEvents" flat class="mt-4 btn-border" @click="getEventsFromMailgun(email.id)">{{ t('getEventsFromMailgun') }}</v-btn>
                    <p v-else class="pt-4">
                      <v-progress-circular indeterminate color="primary" :size="16" width="3" class="mr-2"></v-progress-circular>
                      <small><i>{{ t('isUpdatingEvents') }}</i></small>
                    </p>
                  </template>
                  <template v-else>
                    <p><small><i>{{ t('eventsOnlyAvailableForMailgun') }}</i></small></p>
                  </template>

                </v-card-text>
              </v-card>
            </v-tabs-window-item>

          </v-tabs-window>
        </div>

      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="sendReminderDialog" max-width="600px">
    <v-card>
      <template v-slot:text>
        <h3 class="mb-4">{{ t('sendReminderFor') }}: {{ invoice.number }}</h3>
        <p class="mb-4" style="font-size: 1rem">{{ t('sendReminderConfirmation') }}</p>
        <v-text-field
          v-model="reminderTo"
          :label="t('sentTo')"
          variant="underlined"
          required
        ></v-text-field>
        <v-text-field
          v-model="reminderCc"
          :label="t('cc')"
          variant="underlined"
        ></v-text-field>
        <v-text-field
          v-model="reminderBcc"
          :label="t('bcc')"
          variant="underlined"
        ></v-text-field>
        <v-alert v-if="emailsAreSentToLoggedInUser" type="info" density="compact" class="mt-5">
            {{ t('emailWillBeSentToLoggedInUser', {'emailAddress':userEmailAddress}) }}
        </v-alert>
      </template>
      <template v-slot:actions>
        <v-btn
          :text="t('send')"
          :loading="formSubmitted"
          @click="submitSendReminder"
        />
        <v-btn
          :text="t('cancel')"
          :disabled="formSubmitted"
          @click="sendReminderDialog = false"
        />
      </template>
    </v-card>
  </v-dialog>

  <v-dialog v-model="resendEmailDialog" max-width="600px">
    <v-card>
      <template v-slot:text>
        <h3 v-if="emails.length > 0" class="mb-4">{{ t('resendEmailFor') }}: {{ invoice.number }}</h3>
        <h3 v-else class="mb-4">{{ t('sendEmailFor') }}: {{ invoice.number }}</h3>
        <table class="email-details-table mb-4">
          <tbody>
          <tr v-if="emails.length > 0">
            <td class="nowrap font-bold">{{ t('subject') }}:</td>
            <td>{{ subject }}</td>
          </tr>
          <tr v-if="emails.length > 0">
            <td class="nowrap font-bold">{{ t('attachments') }}:</td>
            <td>
              <span v-for="(attachment, index) in attachments" :key="index">
                <template v-if="attachment.disposition === 'attachment'">{{ attachment.name }}<br></template>
              </span>
            </td>
          </tr>
          </tbody>
        </table>
        <v-text-field
          v-model="to"
          :label="t('sentTo')"
          variant="underlined"
          required
        ></v-text-field>
        <v-text-field
          v-model="cc"
          :label="t('cc')"
          variant="underlined"
          required
        ></v-text-field>
        <v-text-field
          v-model="bcc"
          :label="t('bcc')"
          variant="underlined"
          required
        ></v-text-field>
        <v-alert v-if="emailsAreSentToLoggedInUser" type="info" density="compact" class="mt-5">
            {{ t('emailWillBeSentToLoggedInUser', {'emailAddress':userEmailAddress}) }}
        </v-alert>
      </template>
      <template v-slot:actions>
        <v-btn
          :text="t('send')"
          :loading="formSubmitted"
          @click="submitResendEmail"
        />
        <v-btn
          :text="t('cancel')"
          :disabled="formSubmitted"
          @click="resendEmailDialog = false"
        />
      </template>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import SayErrorResponse from '@/mixins/sayErrorResponse';
import Say from '@/utils/Say'
import { getEmails, resendEmail, updateEvents } from '@/api/emails';
import { useAuth } from '@/utils/auth';
import { sendProFormaReminder } from '@/api/proFormas';

const {t} = useI18n()
const auth = useAuth()

const emit = defineEmits(['refreshInvoices'])

const emailsAreSentToLoggedInUser = inject('emailsAreSentToLoggedInUser', false)
const userEmailAddress = inject('userEmailAddress', false)

const tab = ref('option-1')
const innerTab = ref('html')

const props = defineProps({
  invoice: {
    type: Object,
    required: true
  }
})

const initialLoad = ref(true)
const emailDialog = ref(false)
const resendEmailDialog = ref(false)
const sendReminderDialog = ref(false)
const emails = ref([])

const formSubmitted = ref(false)
const to = ref('')
const cc = ref('')
const bcc = ref('')
const subject = ref('')
const attachments = ref([])
const reminderTo = ref('')
const reminderCc = ref('')
const reminderBcc = ref('')

const isManuallyUpdatingEvents = ref(false)

// A reminder can only be sent from REMINDER_AFTER_DAYS days after the pro forma was sent (status_date is set when the status became PENDING)
const REMINDER_AFTER_DAYS = 8

const reminderAvailableFrom = computed(() => {
  if (!props.invoice.status_date) return null
  const date = new Date(props.invoice.status_date)
  date.setHours(0, 0, 0, 0)
  date.setDate(date.getDate() + REMINDER_AFTER_DAYS)
  return date
})

const canSendReminder = computed(() => {
  return reminderAvailableFrom.value !== null && new Date() >= reminderAvailableFrom.value
})

const openEmailDialog = (invoice: {
  number: any; id: any;
}) => {
  let params = {};
  if (invoice.type === 'pro_forma') {
    params = {'pro_forma_id': invoice.id};
  } else if (invoice.type === 'sales_invoice') {
    params = {'sales_invoice_id': invoice.id};
  } else if (invoice.type === 'credit_note') {
    params = {'credit_note_id': invoice.id};
  }
  // Get pro forma emails
  getEmails(params).then((response) => {
    let responseData = response.data;
    if (responseData.length !== 0) {
      // Loop through emails and replace the image with class 'logo-vaf-infra' with the VAF Infra logo
      responseData.forEach((email: { body_html: string }) => {
        email.body_html = email.body_html.replace(/<img src=".*" alt="VAF-Infra" class="logo-vaf-infra">/g, '<img class="logo-vaf-infra" alt="VAF-Infra" src="/src/assets/logo-van-amerongen.svg">');
        email.body_html = email.body_html.replace(/<style>/g, '<style scoped>');
        // Replace new lines with <br> tags in body_text
        email.body_text = email.body_text.replace(/\n/g, '<br>');
        // Loop through the events and group them by event.recipient
        let groupedEvents = {};
        email.events.forEach((event: { recipient: string }) => {
          if (!groupedEvents[event.recipient]) {
            groupedEvents[event.recipient] = [];
          }
          groupedEvents[event.recipient].push(event);
        });
        email.groupedEvents = groupedEvents;
      });
      emails.value = responseData;
      
      if(initialLoad.value) {
        initialLoad.value = false;
        tab.value = responseData[0].id;
      }
    }
    if(responseData.length !== 0) {
      to.value = responseData[0].to.map((to: { address: string }) => to.address).join(', ');
      cc.value = responseData[0].cc.map((cc: { address: string }) => cc.address).join(', ');
      bcc.value = responseData[0].bcc.map((bcc: { address: string }) => bcc.address).join(', ');
      subject.value = responseData[0].subject;
      attachments.value = responseData[0].attachments;
    }
    emailDialog.value = true;
  }).catch((error) => {
    SayErrorResponse(error);
  });
}

const submitResendEmail = () => {
  formSubmitted.value = true;
  let params = {
    to: to.value,
    cc: cc.value,
    bcc: bcc.value,
  };
  if (props.invoice.type === 'pro_forma') {
    // add pro_forma_id to params
    params = {...params, pro_forma_id: props.invoice.id};
  } else if (props.invoice.type === 'sales_invoice') {
    // add sales_invoice_id to params
    params = {...params, sales_invoice_id: props.invoice.id};
  } else if (props.invoice.type === 'credit_note') {
    // add credit_note_id to params
    params = {...params, credit_note_id: props.invoice.id};
  }
  // Send the email
  resendEmail(params).then(() => {
    // Delay the opening of the email dialog to give the email some time to be sent
    setTimeout(() => {
      // Close the dialog
      resendEmailDialog.value = false;
      // Show a success message
      Say('success', t('emailSent'));
      // Reload the emails
      initialLoad.value = true;
      innerTab.value = 'html';
      openEmailDialog(props.invoice);
      formSubmitted.value = false;
      // Emit an event to refresh the pro forma list
      emit('refreshInvoices');
    }, 2000);
  }).catch((error) => {
    formSubmitted.value = false;
    SayErrorResponse(error);
  });
}

// Prefill the reminder recipients with the recipients of the latest email
const openSendReminderDialog = () => {
  const latestEmail = emails.value[0];
  reminderTo.value = latestEmail ? latestEmail.to.map((to: { address: string }) => to.address).join(', ') : '';
  reminderCc.value = latestEmail ? latestEmail.cc.map((cc: { address: string }) => cc.address).join(', ') : '';
  reminderBcc.value = latestEmail ? latestEmail.bcc.map((bcc: { address: string }) => bcc.address).join(', ') : '';
  sendReminderDialog.value = true;
}

const submitSendReminder = () => {
  formSubmitted.value = true;
  sendProFormaReminder(props.invoice.id, {
    to: reminderTo.value,
    cc: reminderCc.value,
    bcc: reminderBcc.value,
  }).then(() => {
    // Delay the opening of the email dialog to give the email some time to be sent
    setTimeout(() => {
      sendReminderDialog.value = false;
      Say('success', t('reminderSent'));
      // Reload the emails
      initialLoad.value = true;
      innerTab.value = 'html';
      openEmailDialog(props.invoice);
      formSubmitted.value = false;
      // Emit an event to refresh the pro forma list
      emit('refreshInvoices');
    }, 2000);
  }).catch((error) => {
    formSubmitted.value = false;
    SayErrorResponse(error);
  });
}

const getEventsFromMailgun = (emailId: number) => {
  if(isManuallyUpdatingEvents.value) return;
  isManuallyUpdatingEvents.value = true;
  updateEvents(emailId).then(() => {
    Say('success', t('eventsUpdated'));
    // Reload the emails
    openEmailDialog(props.invoice);
    // Emit an event to refresh the pro forma list
    emit('refreshInvoices');
    isManuallyUpdatingEvents.value = false;
  }).catch((error) => {
    isManuallyUpdatingEvents.value = false;
    SayErrorResponse(error);
  });
}

const refreshData = () => {
  openEmailDialog(props.invoice)
  emit('refreshInvoices')
}

const formatDateTime = (dateTimeString: string) => {
  if (! dateTimeString) return ''
  dateTimeString = dateTimeString.toString()
  let parts = []
  if (dateTimeString.includes('T')) {
    parts = dateTimeString.split('T')
  } else {
    parts = dateTimeString.split(' ')
  }
  const date = parts[0]
  const time = parts[1]
  const dateParts = date.split('-')
  const timeParts = time.split(':')
  return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]} ${timeParts[0]}:${timeParts[1]}`
}

const formatDate = (date: Date) => {
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`
}

const formatDateV2 = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
}

const formatDateTimeV2 = (dateTime: string) => {
  let [date, time] = dateTime.split('T');
  // remove .000000Z from time
  time = time.slice(0, -8);
  return `${formatDateV2(date)} ${t('at').toLowerCase()} ${time}`;
}
</script>

<style scoped>
.text-orange {
  color: rgb(245, 120, 40) !important;
}

.font-bold {
  font-weight: bold;
}

.email-html,
.email-plain-text {
  width: 780px;
}

:deep(.email-html p) {
  margin: 1em 0 !important;
}

.text-lg {
  font-size: 1.25rem;
}

.email-details-table {
  padding-bottom: 0.5em;
}

.email-details-table td {
  padding: 0.25em 1em 0.25em 0;
  vertical-align: top;
}

.uppercase {
  text-transform: uppercase;
}

.nowrap {
  white-space: nowrap;
}

.tabs-emails {
  width: 316px;
}

.inner-tab-window {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.btn-border {
    border: 1px solid #ddd;
}
</style>
